import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
    Box, Typography, Button, Paper, IconButton, TextField, InputAdornment, 
    Tabs, Tab, Snackbar, Alert, CircularProgress, Checkbox, 
    Tooltip 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';

import RoleFormModal from '../../components/RoleFormModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';

import RolesTable from './RolesTable';
import PermissionsMatrix from './PermissionsMatrix';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`permissions-tabpanel-${index}`}
            aria-labelledby={`permissions-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 0 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function Permissions() {
    const permissionsList = useSelector(state => state.permissions.list) || [];
    const roles = useSelector(state => state.users.roles) || [];
    const isLoading = useSelector(state => state.permissions.isLoading);
    const dispatch = useDispatch();

    const [tabValue, setTabValue] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, roleId: null });
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    
    // Matrix inline editing state
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [draftPermissions, setDraftPermissions] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Removed global localMatrix sync effect as we now use draftPermissions for inline edits

    useEffect(() => {
        if (tabValue === 0) {
            dispatch.users.fetchRolesAsync({ page, limit: rowsPerPage, search: searchTerm });
        } else if (tabValue === 1) {
            dispatch.permissions.fetchPermissionsAsync({ noPagination: true, search: searchTerm });
            // Fetch all roles for the matrix columns
            dispatch.users.fetchRolesAsync({ noPagination: true }); 
        }
    }, [tabValue, page, rowsPerPage, searchTerm, dispatch]);

    const handleRefresh = () => {
        if (tabValue === 0) {
            dispatch.users.fetchRolesAsync({ page, limit: rowsPerPage, search: searchTerm });
        } else if (tabValue === 1) {
            dispatch.permissions.fetchPermissionsAsync({ noPagination: true, search: searchTerm });
            dispatch.users.fetchRolesAsync({ noPagination: true }); 
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setPage(0);
    };

    const handleOpenAddRole = () => {
        setEditingRole(null);
        setModalOpen(true);
    };

    const handleOpenEditRole = (role) => {
        setEditingRole(role);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingRole(null);
    };

    const handleSaveRole = async (roleData) => {
        try {
            if (editingRole) {
                await dispatch.permissions.updateRoleAsync({ id: editingRole.id, data: roleData });
                setNotification({ open: true, message: 'Role updated successfully', severity: 'success' });
            } else {
                await dispatch.permissions.createRoleAsync(roleData);
                setNotification({ open: true, message: 'Role created successfully', severity: 'success' });
            }
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to save role', severity: 'error' });
            throw error; // Let modal stay open if needed
        }
    };

    const handleDeleteClick = (id) => {
        setDeleteDialog({ open: true, roleId: id });
    };

    const confirmDelete = async () => {
        if (deleteDialog.roleId) {
            try {
                await dispatch.permissions.deleteRoleAsync(deleteDialog.roleId);
                setNotification({ open: true, message: 'Role deleted successfully', severity: 'success' });
            } catch (error) {
                setNotification({ open: true, message: error.message || 'Failed to delete role', severity: 'error' });
            }
        }
        setDeleteDialog({ open: false, roleId: null });
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({ open: false, roleId: null });
    };

    const filteredPermissions = permissionsList.filter(p => {
        const search = searchTerm.toLowerCase();
        const keyMatch = p.key ? String(p.key).toLowerCase().includes(search) : false;
        const nameMatch = p.name ? String(p.name).toLowerCase().includes(search) : false;
        const descMatch = p.description ? String(p.description).toLowerCase().includes(search) : false;
        const catMatch = p.category ? String(p.category).toLowerCase().includes(search) : false;
        return keyMatch || nameMatch || descMatch || catMatch;
    });




    const handleStartEdit = (role) => {
        setEditingRoleId(role.id);
        const cleanPerms = (role.permissions || []).map(p => typeof p === 'string' ? p : (p?.key || p?.name || p?.code || '')).filter(Boolean);
        setDraftPermissions(cleanPerms);
    };

    const handleCancelEdit = () => {
        setEditingRoleId(null);
        setDraftPermissions([]);
    };

    const handleTogglePermission = (permissionKey) => {
        setDraftPermissions(prev => {
            const cleanPrev = prev.map(p => typeof p === 'string' ? p : (p?.key || p?.name || p?.code || '')).filter(Boolean);
            return cleanPrev.includes(permissionKey)
                ? cleanPrev.filter(k => k !== permissionKey)
                : [...cleanPrev, permissionKey];
        });
    };

    const handleSaveColumn = async (roleId) => {
        setIsSaving(true);
        try {
            // Payload requirement: { "roles": [{ "id": "...", "permissions": [...] }] }
            const payload = {
                roles: [{
                    id: roleId,
                    permissions: draftPermissions
                }]
            };
            
            await dispatch.permissions.updatePermissionsMatrixAsync(payload);
            setNotification({ open: true, message: 'Permissions updated successfully', severity: 'success' });
            setEditingRoleId(null);
            setDraftPermissions([]);
        } catch (error) {
            console.error('Failed to save permissions:', error);
            setNotification({ open: true, message: error.message || 'Failed to update permissions', severity: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ fontSize: '1.45rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
                    Access Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Button 
                        variant="outlined" 
                        onClick={handleRefresh}
                        startIcon={
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10" />
                                <polyline points="1 20 1 14 7 14" />
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                            </svg>
                        }
                        sx={{
                            borderColor: '#e2e8f0',
                            bgcolor: '#ffffff',
                            color: '#334155',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            height: 36,
                            borderRadius: '8px',
                            px: 1.75,
                            boxShadow: 'none',
                            '&:hover': {
                                borderColor: '#cbd5e1',
                                bgcolor: '#f8fafc',
                            },
                        }}
                    >
                        Refresh
                    </Button>
                    {tabValue === 0 && (
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={handleOpenAddRole}
                            startIcon={
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                            }
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                height: 36,
                                borderRadius: '8px',
                                px: 2,
                                boxShadow: 'none',
                            }}
                        >
                            Create Role
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ borderBottom: '1px solid #eaecf0', mb: 3 }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="permissions tabs"
                    sx={{
                        minHeight: 40,
                        '& .MuiTabs-indicator': {
                            backgroundColor: 'primary.main',
                            height: 2.5,
                            borderRadius: '2px 2px 0 0',
                        },
                    }}
                >
                    <Tab 
                        label="Roles" 
                        sx={{
                            textTransform: 'none',
                            fontWeight: tabValue === 0 ? 600 : 500,
                            fontSize: '0.875rem',
                            color: tabValue === 0 ? 'primary.main' : '#64748b',
                            minHeight: 40,
                            py: 1,
                            px: 1.5,
                            mr: 2,
                            minWidth: 'auto',
                            '&.Mui-selected': {
                                color: 'primary.main',
                            },
                        }}
                    />
                    <Tab 
                        label="Permissions Matrix" 
                        sx={{
                            textTransform: 'none',
                            fontWeight: tabValue === 1 ? 600 : 500,
                            fontSize: '0.875rem',
                            color: tabValue === 1 ? 'primary.main' : '#64748b',
                            minHeight: 40,
                            py: 1,
                            px: 1.5,
                            minWidth: 'auto',
                            '&.Mui-selected': {
                                color: 'primary.main',
                            },
                        }}
                    />
                </Tabs>
            </Box>

            {/* ROLES TAB */}
            <TabPanel value={tabValue} index={0}>
                <RolesTable 
                    roles={roles} 
                    isLoading={isLoading} 
                    onEdit={handleOpenEditRole} 
                    onDelete={handleDeleteClick}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    totalRoles={useSelector(state => state.users.rolesTotal) || 0}
                />
            </TabPanel>

            {/* PERMISSIONS MATRIX TAB */}
            <TabPanel value={tabValue} index={1}>
                <PermissionsMatrix 
                    roles={roles}
                    permissionsList={permissionsList}
                    isLoading={isLoading}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    editingRoleId={editingRoleId}
                    draftPermissions={draftPermissions}
                    isSaving={isSaving}
                    onStartEdit={handleStartEdit}
                    onCancelEdit={handleCancelEdit}
                    onTogglePermission={handleTogglePermission}
                    onSaveColumn={handleSaveColumn}
                />
            </TabPanel>

            <RoleFormModal
                open={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveRole}
                editingRole={editingRole}
            />

            <ConfirmDeleteModal
                open={deleteDialog.open}
                onClose={closeDeleteDialog}
                onConfirm={confirmDelete}
                itemName="role"
            />

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
